import { useEffect, useMemo, useState } from "react";
import { api, frDate, openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { fr as frLocale } from "date-fns/locale";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, CalendarDays, MapPin, Users, Clock, Tag, FileDown, Image as ImageIcon,
} from "lucide-react";

const empty = {
  title: "", description: "", location: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  event_type: "programme", status: "a_venir",
  organizer: "", expected_attendees: 0, notes: "",
};

const TYPES = {
  programme: { label: "Programme", bg: "bg-brand-navy/10 text-brand-navy" },
  reunion: { label: "Réunion", bg: "bg-blue-100 text-blue-700" },
  formation: { label: "Formation", bg: "bg-amber-100 text-amber-700" },
  collecte: { label: "Collecte", bg: "bg-brand-green/10 text-brand-green" },
  atelier: { label: "Atelier", bg: "bg-purple-100 text-purple-700" },
  festival: { label: "Festival", bg: "bg-brand-orange/10 text-brand-orange" },
  autre: { label: "Autre", bg: "bg-gray-100 text-gray-700" },
};
const STATUS = {
  a_venir: { label: "À venir", bg: "bg-brand-orange text-white" },
  en_cours: { label: "En cours", bg: "bg-brand-green text-white" },
  termine: { label: "Terminé", bg: "bg-gray-200 text-gray-700" },
  annule: { label: "Annulé", bg: "bg-red-100 text-red-700" },
};

export default function Evenements() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [month, setMonth] = useState(new Date());

  const load = () => api.get("/events").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.start_date) { toast.error("Titre et date requis"); return; }
    try {
      const payload = {
        ...form,
        expected_attendees: Number(form.expected_attendees) || 0,
      };
      if (edit) await api.put(`/events/${edit.id}`, payload);
      else await api.post("/events", payload);
      toast.success(edit ? "Événement modifié." : "Événement créé.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Supprimer "${e.title}" ?`)) return;
    await api.delete(`/events/${e.id}`); load();
  };

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const key = (r.start_date || "").slice(0, 10);
      if (!m[key]) m[key] = [];
      m[key].push(r);
    }
    return m;
  }, [rows]);

  const markedDates = useMemo(
    () => Object.keys(eventsByDay).map((k) => new Date(k)).filter((d) => !isNaN(d.getTime())),
    [eventsByDay]
  );

  const upcoming = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return [...rows]
      .filter((r) => r.status !== "annule" && new Date(r.start_date) >= today)
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [rows]);

  const past = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return [...rows]
      .filter((r) => new Date(r.start_date) < today)
      .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  }, [rows]);

  return (
    <div data-testid="evenements-page">
      <PageHeader
        eyebrow="Agenda"
        title="Calendrier des événements & programmes"
        description="Planifiez les programmes, ateliers et moments forts de la vie associative."
        actions={
          <div className="flex gap-2">
            <Button
              data-testid="export-calendar-btn"
              variant="outline"
              onClick={() => openPdf(`/events/calendar-pdf?year=${month.getFullYear()}&month=${month.getMonth() + 1}`)}
            >
              <FileDown size={16} className="mr-1" /> PDF du mois
            </Button>
            <Button data-testid="new-event-btn" onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              <Plus size={16} className="mr-1" /> Nouvel événement
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="bg-white border border-brand-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays size={18} className="text-brand-navy" />
            <h3 className="font-serif text-xl text-brand-navy">Calendrier</h3>
          </div>
          <Calendar
            mode="single"
            locale={frLocale}
            month={month}
            onMonthChange={setMonth}
            modifiers={{ hasEvent: markedDates }}
            modifiersClassNames={{
              hasEvent: "bg-brand-orange/15 text-brand-orange font-semibold rounded-md",
            }}
            className="rounded-md"
          />
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <span className="w-3 h-3 rounded bg-brand-orange/20 inline-block"></span>
            Jour avec événement
          </div>
        </div>

        {/* Upcoming list */}
        <div className="lg:col-span-2 bg-white border border-brand-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="small-caps">Prochainement</div>
              <h3 className="font-serif text-2xl text-brand-navy">À venir</h3>
            </div>
            <Badge className="bg-brand-navy text-white">{upcoming.length} événement(s)</Badge>
          </div>

          {upcoming.length === 0 ? (
            <div className="text-center py-14 text-gray-400 border border-dashed border-brand-border rounded-lg">
              Aucun événement à venir pour le moment.
            </div>
          ) : (
            <ul className="space-y-3">
              {upcoming.map((e) => (
                <li key={e.id} data-testid={`event-card-${e.id}`} className="border border-brand-border rounded-lg p-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-16 text-center bg-brand-navy text-white rounded-md py-2">
                      <div className="text-[10px] uppercase tracking-widest opacity-70">
                        {new Date(e.start_date).toLocaleDateString("fr-FR", { month: "short" })}
                      </div>
                      <div className="font-serif text-2xl leading-none">
                        {new Date(e.start_date).getDate()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-serif text-lg text-brand-navy truncate">{e.title}</h4>
                        <Badge className={TYPES[e.event_type]?.bg}>
                          <Tag size={11} className="mr-1" />
                          {TYPES[e.event_type]?.label || e.event_type}
                        </Badge>
                        <Badge className={STATUS[e.status]?.bg}>
                          {STATUS[e.status]?.label}
                        </Badge>
                      </div>
                      {e.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{e.description}</p>}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                        {e.location && <span className="inline-flex items-center gap-1"><MapPin size={12} />{e.location}</span>}
                        {e.end_date && <span className="inline-flex items-center gap-1"><Clock size={12} />Jusqu'au {frDate(e.end_date)}</span>}
                        {e.expected_attendees > 0 && <span className="inline-flex items-center gap-1"><Users size={12} />{e.expected_attendees} pers.</span>}
                        {e.organizer && <span>Organisé par {e.organizer}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button data-testid={`poster-${e.id}`} size="sm" variant="outline" onClick={() => openPdf(`/events/${e.id}/poster-pdf`)}>
                        <ImageIcon size={14} className="mr-1" /> Affiche
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEdit(e); setForm({ ...empty, ...e }); setOpen(true); }}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(e)}><Trash2 size={14} className="text-red-500" /></Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {past.length > 0 && (
        <div className="bg-white border border-brand-border rounded-xl p-5 mt-6">
          <div className="small-caps">Historique</div>
          <h3 className="font-serif text-2xl text-brand-navy mb-3">Événements passés</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 small-caps">
                <tr>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Titre</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Lieu</th>
                  <th className="text-left py-3 px-4">Statut</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {past.map((e) => (
                  <tr key={e.id} className="border-t border-brand-border hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">{frDate(e.start_date)}</td>
                    <td className="py-3 px-4 font-medium text-gray-900">{e.title}</td>
                    <td className="py-3 px-4"><Badge className={TYPES[e.event_type]?.bg}>{TYPES[e.event_type]?.label}</Badge></td>
                    <td className="py-3 px-4 text-gray-700">{e.location || "-"}</td>
                    <td className="py-3 px-4"><Badge className={STATUS[e.status]?.bg}>{STATUS[e.status]?.label}</Badge></td>
                    <td className="py-3 px-4 text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openPdf(`/events/${e.id}/poster-pdf`)}><ImageIcon size={14} className="mr-1" />Affiche</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEdit(e); setForm({ ...empty, ...e }); setOpen(true); }}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(e)}><Trash2 size={14} className="text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2"><Label>Titre *</Label><Input data-testid="event-title-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Forum Jeunesse 2026" /></div>
            <div><Label>Type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({...form, event_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date de début *</Label><Input type="date" data-testid="event-start-input" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>Date de fin</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Lieu</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Salle polyvalente, La Roche-sur-Yon" /></div>
            <div><Label>Organisateur</Label><Input value={form.organizer} onChange={(e) => setForm({...form, organizer: e.target.value})} placeholder="Association AIJ" /></div>
            <div><Label>Participants attendus</Label><Input type="number" value={form.expected_attendees} onChange={(e) => setForm({...form, expected_attendees: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Notes internes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-event-btn" onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer l'événement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
